import type { QueryFunctionContext, QueryKey, UseQueryOptions } from '@tanstack/react-query';
import {
  getUsStates,
  type GeneralUSStatesParams,
  GeneralAddressesParams,
  getAddresses,
  GeneralCompanyDetailsByVATParams,
  getCompanyDetailsByVAT,
  GeneralUKAddressListParams,
  getUKAddressList,
  GeneralUKAddressDetailsParams,
  getUKAddressDetails,
  GetGeoLocationParams,
  getGeoLocation,
} from './general.api';
import type { OptionalQueryOptions, QueryApiOptions } from '../../types/types';
import { AddressFieldsType, GeoLocation, UKAddress, USState, VatInformation } from '@as-react/common-types';

export const generalQueryKeys = {
  all: () => ['general'] as QueryKey,
  USStates: () => [...generalQueryKeys.all(), 'USStates'] as QueryKey,
  addresses: (params: GeneralAddressesParams) => [...generalQueryKeys.all(), 'addresses', params] as QueryKey,
  getGeoLocation: (params: GetGeoLocationParams) => [...generalQueryKeys.all(), 'geoLocation', params] as QueryKey,
  companyDetailsByVAT: (params: GeneralCompanyDetailsByVATParams) =>
    [...generalQueryKeys.all(), 'companyDetailsByVAT', params] as QueryKey,
  UKAddressList: (params: GeneralUKAddressListParams) =>
    [...generalQueryKeys.all(), 'UKAddressList', params] as QueryKey,
  UKAddressDetails: (params: GeneralUKAddressDetailsParams) =>
    [...generalQueryKeys.all(), 'UKAddressDetails', params] as QueryKey,
};

export const generalQueries = (apiOptions: QueryApiOptions) => ({
  getUSStates: (params: GeneralUSStatesParams, options: OptionalQueryOptions<Array<USState>> = {}) =>
    ({
      ...options,
      queryKey: generalQueryKeys.USStates(),
      queryFn: () => getUsStates(apiOptions, params),
    } as UseQueryOptions<Array<USState>>),
  getAddresses: (params: GeneralAddressesParams, options: OptionalQueryOptions<Array<AddressFieldsType>> = {}) =>
    ({
      ...options,
      queryKey: generalQueryKeys.addresses(params),
      queryFn: () => getAddresses(apiOptions, params),
    } as UseQueryOptions<Array<AddressFieldsType>>),
  getGeoLocation: (
    params: GetGeoLocationParams,
    options: OptionalQueryOptions<GeoLocation> = {}
  ): UseQueryOptions<GeoLocation> => ({
    ...options,
    queryKey: generalQueryKeys.getGeoLocation(params),
    queryFn: () => getGeoLocation(apiOptions, params),
  }),
  getCompanyDetailsByVAT: (
    params: GeneralCompanyDetailsByVATParams,
    options: OptionalQueryOptions<VatInformation> = {}
  ) =>
    ({
      ...options,
      queryKey: generalQueryKeys.companyDetailsByVAT(params),
      queryFn: () => getCompanyDetailsByVAT(apiOptions, params),
    } as UseQueryOptions<VatInformation>),
  getUKAddressList: <TReturnData>(
    params: GeneralUKAddressListParams,
    options: OptionalQueryOptions<Array<UKAddress>, TReturnData> = {}
  ): UseQueryOptions<Array<UKAddress>, Error, TReturnData> => ({
    ...options,
    queryKey: generalQueryKeys.UKAddressList(params),
    queryFn: (context: QueryFunctionContext) => getUKAddressList(apiOptions, params, context),
  }),
  getUKAddressDetails: (
    params: GeneralUKAddressDetailsParams,
    options: OptionalQueryOptions<AddressFieldsType> = {}
  ): UseQueryOptions<AddressFieldsType> => ({
    ...options,
    queryKey: generalQueryKeys.UKAddressDetails(params),
    queryFn: () => getUKAddressDetails(apiOptions, params),
  }),
});
